import { useState, useEffect, useCallback } from 'react';
import { getChangelogs } from '@/lib/changelog-loader';

const STORAGE_KEY_SEEN = 'changelog_last_seen_version';
const STORAGE_KEY_DIALOG = 'changelog_last_dialog_version';

/**
 * Hook to manage changelog notifications and dialog visibility.
 * 
 * Checks local storage to see if the user has seen the latest version.
 * Syncs state across tabs/components using window events.
 * 
 * @param audience - The audience to filter changelogs for.
 */
export const useChangelogNotification = (audience: 'customer' | 'internal' = 'customer') => {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  // Initialize suppressed state based on URL param to avoid flash
  const [isSuppressed, setIsSuppressed] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('success') === 'true';
    }
    return false;
  });

  // Listen for suppression events
  useEffect(() => {
    const handleSuppress = (e: CustomEvent<{ suppressed: boolean }>) => {
      setIsSuppressed(e.detail?.suppressed ?? false);
    };
    window.addEventListener('changelog-suppress' as any, handleSuppress);
    return () => window.removeEventListener('changelog-suppress' as any, handleSuppress);
  }, []);

  /**
   * Helper to compare semantic versions (v1 > v2).
   */
  const isNewer = (v1: string, v2: string) => {
    const p1 = v1.split('.').map(Number);
    const p2 = v2.split('.').map(Number);
    for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
        const n1 = p1[i] || 0;
        const n2 = p2[i] || 0;
        if (n1 > n2) return true;
        if (n1 < n2) return false;
    }
    return false;
  };

  const checkStatus = useCallback(() => {
    const logs = getChangelogs(audience);
    if (logs.length === 0) return;

    const latest = logs[0].version;
    setLatestVersion(latest);

    const lastSeen = localStorage.getItem(STORAGE_KEY_SEEN);
    const lastDialog = localStorage.getItem(STORAGE_KEY_DIALOG);

    // New Version Indicator Logic
    if (!lastSeen || isNewer(latest, lastSeen)) {
      setHasNewVersion(true);
    } else {
      setHasNewVersion(false);
    }

    // Dialog Logic
    // Show if we have a new version (relative to what was last shown in dialog)
    // AND it's strictly newer than the last dialog version
    // AND it is not suppressed
    if ((!lastDialog || isNewer(latest, lastDialog)) && !isSuppressed) {
        // Only show dialog if we truly have a new version
        setShowDialog(true);
    } else {
        setShowDialog(false);
    }
  }, [audience, isSuppressed]);

  useEffect(() => {
    checkStatus();

    // Listen for local updates (custom event)
    const handleLocalUpdate = () => checkStatus();
    window.addEventListener('changelog-state-update', handleLocalUpdate);
    
    return () => {
      window.removeEventListener('changelog-state-update', handleLocalUpdate);
    };
  }, [checkStatus]);

  const markAsSeen = useCallback(() => {
    if (!latestVersion) return;
    localStorage.setItem(STORAGE_KEY_SEEN, latestVersion);
    setHasNewVersion(false);
    // Notify other hook instances
    window.dispatchEvent(new Event('changelog-state-update'));
  }, [latestVersion]);

  const closeDialog = useCallback(() => {
    if (!latestVersion) return;
    localStorage.setItem(STORAGE_KEY_DIALOG, latestVersion);
    setShowDialog(false);
    // Notify other hook instances (though dialog state is local usually, persistence matters)
    window.dispatchEvent(new Event('changelog-state-update'));
  }, [latestVersion]);

  return {
    hasNewVersion,
    showDialog,
    latestVersion,
    markAsSeen,
    closeDialog
  };
};
